/**
 * Relay wipe — full-screen color blocks im "Frage → ALLYVER" Rhythmus.
 *
 *   Frage-Block  →  Wisch  →  ALLYVER-Block  →  Wisch  →  Frage-Block  →  …
 *
 * Jede Frage aus `inputs.items` wird automatisch von einem ALLYVER-Block
 * gefolgt. **Jeder einzelne ALLYVER-Block** kann individuell konfiguriert
 * werden über `extras.allyverSlots[N]` (N = Index der Frage davor, 0-based):
 *
 *   { mode: "text"  }                                  → großes "ALLYVER"-Wordmark
 *   { mode: "logo"  }                                  → Allyver-Logo PNG
 *   { mode: "card", media, name, subtitle }            → Bild oben, Name + Subtitle drunter (clean)
 *   { mode: "image", media, name, subtitle }           → Full-Bleed Bild + Name-Overlay unten (cinematic)
 *   { mode: "editorial", media, name, subtitle }       → großes Bild + Name + „ALLYVER"-Footer (Magazine)
 *
 * Fehlt `allyverSlots[N]`, greift der globale Fallback aus
 * `extras.allyverAsLogo` (false = text, true = logo).
 */
import type { SceneBuilder } from "../../reel/scenes.js";
import { brandBackdrop } from "../../primitives/brandBackdrop.js";
import {
  accentFor, clipOutroFade, combineSceneFragments, escapeHtml, makeTl,
  typeSize, weights, withAlpha, colors as themeColors, gsapSetLine,
  brandName, resolveBrandLogoSrc, pickMediaSrc,
} from "../../reel/sceneShared.js";

type AllyverMode = "text" | "logo" | "card" | "image" | "editorial";

type AllyverSlotCfg = {
  mode: AllyverMode;
  media?: string;
  name?: string;
  subtitle?: string;
};

type Block =
  | { kind: "question"; text: string }
  | { kind: "allyver"; slot: AllyverSlotCfg };

const DEFAULT_QUESTIONS = [
  "Du hast Hunger?",
  "Du suchst ein Auto?",
  "Du willst zum Friseur?",
  "Du brauchst ein Taxi?",
];

const ALLYVER_MODES: AllyverMode[] = ["text", "logo", "card", "image", "editorial"];

function isAllyverMode(s: unknown): s is AllyverMode {
  return typeof s === "string" && (ALLYVER_MODES as readonly string[]).includes(s);
}

function readAllyverSlots(raw: unknown): AllyverSlotCfg[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((entry): AllyverSlotCfg => {
    if (!entry || typeof entry !== "object") return { mode: "text" };
    const obj = entry as Record<string, unknown>;
    const mode = isAllyverMode(obj.mode) ? obj.mode : "text";
    const media = typeof obj.media === "string" ? obj.media : undefined;
    const name = typeof obj.name === "string" ? obj.name : undefined;
    const subtitle = typeof obj.subtitle === "string" ? obj.subtitle : undefined;
    return { mode, media, name, subtitle };
  });
}

export const colorBlockReveal: SceneBuilder = ({ ctx, clipPrefix, inputs, clipDurationSec, clipStartSec }) => {
  const accent = accentFor(inputs);

  const rawItems = inputs.items?.length ? inputs.items : DEFAULT_QUESTIONS;
  const questions = rawItems
    .map((q) => (typeof q === "string" ? q.trim() : ""))
    .filter((q) => {
      if (!q) return false;
      const upper = q.toUpperCase();
      if (upper === "ALLYVER" || upper === "ALLYVER.") return false;
      if (q === "@logo" || upper === "@LOGO") return false;
      return true;
    })
    .slice(0, 8);

  const subline = inputs.subtext ?? "";
  const showIndex = inputs.extras?.showIndex !== false;
  const showSlash = inputs.extras?.showSlash !== false;
  const allyverAsLogo = inputs.extras?.allyverAsLogo === true;
  const allyverSlots = readAllyverSlots(inputs.extras?.allyverSlots);

  // Wipe-Richtung: "alternate" = Q von rechts ⇄ A von links (Ping-Pong),
  // "left" / "right" / "up" / "down" = alle Blöcke wischen in dieselbe
  // Richtung rein.
  const rawWipe = typeof inputs.extras?.wipeDirection === "string"
    ? String(inputs.extras.wipeDirection).toLowerCase()
    : "alternate";
  const wipeDirection: "alternate" | "left" | "right" | "up" | "down" =
    rawWipe === "left" || rawWipe === "right" || rawWipe === "up" || rawWipe === "down"
      ? rawWipe
      : "alternate";

  const resolveSlot = (questionIdx: number): AllyverSlotCfg => {
    const cfg = allyverSlots[questionIdx];
    if (cfg) return cfg;
    return { mode: allyverAsLogo ? "logo" : "text" };
  };

  const blocks: Block[] = [];
  questions.forEach((q, i) => {
    blocks.push({ kind: "question", text: q });
    blocks.push({ kind: "allyver", slot: resolveSlot(i) });
  });

  const mega = typeSize("mega", ctx.format);
  const lg = typeSize("lg", ctx.format);
  const md = typeSize("md", ctx.format);
  const sm = typeSize("sm", ctx.format);

  // Auto-fit question size based on the longest single word across all
  // questions. "Wochenende" / "Unterkunft" etc. are 10–11 chars and would
  // otherwise hit the frame edges at the default scale.
  const longestWord = questions
    .flatMap((q) => q.split(/\s+/))
    .reduce((max, w) => Math.max(max, w.length), 0);
  const questionScale =
    longestWord >= 12 ? 0.48 :
    longestWord >= 10 ? 0.56 :
    longestWord >= 8 ? 0.64 :
    0.72;
  const questionLetterSpacing =
    longestWord >= 10 ? -2 : longestWord >= 8 ? -2.5 : -3;

  // ALLYVER text scales down a touch when used as wordmark — the letters
  // are taller than the questions so we keep them slightly smaller.
  const allyverScale = brandName.length >= 10 ? 0.7 : brandName.length >= 8 ? 0.78 : 0.88;
  const allyverLetterSpacing = brandName.length >= 10 ? -3 : -5;

  const wrapId = `${clipPrefix}-wrap`;
  const blockIds = blocks.map((_, i) => `${clipPrefix}-b-${i}`);
  const contentIds = blocks.map((_, i) => `${clipPrefix}-c-${i}`);

  const t = makeTl(clipStartSec);
  const backdrop = brandBackdrop(ctx, `${clipPrefix}-bg`, { variant: "solid", accent });
  const logoSrc = resolveBrandLogoSrc();

  /**
   * Rendert den Innenteil eines ALLYVER-Blocks abhängig vom Slot-Mode.
   * Wird in den Color-Block-Wrapper eingebettet (Hintergrund + Padding
   * kommen vom Wrapper-Container).
   */
  const renderAllyverContent = (slot: AllyverSlotCfg): string => {
    const mediaSrc = slot.media ? pickMediaSrc(slot.media) ?? slot.media : undefined;
    const name = slot.name?.trim() ?? "";
    const subtitle = slot.subtitle?.trim() ?? "";

    // Fallback wenn ein visueller Modus ohne Bild gewählt wird → behandle wie Logo / Text
    const wantsMedia = slot.mode === "card" || slot.mode === "image" || slot.mode === "editorial";
    if (wantsMedia && !mediaSrc) {
      // Sanfter Fallback: Logo, falls verfügbar — sonst Text.
      if (logoSrc) {
        return `<img src="${escapeHtml(logoSrc)}" alt="${escapeHtml(brandName)}" style="max-width:68%;max-height:44%;object-fit:contain;" />`;
      }
      return `<div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${mega * allyverScale}px;letter-spacing:${allyverLetterSpacing}px;line-height:0.9;color:${accent};text-align:center;text-wrap:balance;width:100%;">${escapeHtml(brandName.toUpperCase())}</div>`;
    }

    if (slot.mode === "logo") {
      if (logoSrc) {
        return `<img src="${escapeHtml(logoSrc)}" alt="${escapeHtml(brandName)}" style="max-width:68%;max-height:44%;object-fit:contain;" />`;
      }
      // Logo fehlt → Text als Fallback
    }

    if (slot.mode === "card" && mediaSrc) {
      // Clean Card: zentriertes quadratisches Bild + Name + Subtitle drunter
      const imgSize = ctx.format === "9:16" ? 480 : 380;
      return `<div style="display:flex;flex-direction:column;align-items:center;gap:32px;width:100%;max-width:760px;">
        <div style="position:relative;width:${imgSize}px;height:${imgSize}px;border-radius:32px;overflow:hidden;background:${withAlpha(themeColors.fg, 0.06)};box-shadow:0 24px 60px rgba(0,0,0,0.45), 0 0 0 1px ${withAlpha(accent, 0.4)};">
          <img src="${escapeHtml(mediaSrc)}" alt="${escapeHtml(name || brandName)}" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;" />
        </div>
        ${name ? `<div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.85}px;letter-spacing:-1.5px;line-height:1.02;color:${themeColors.fg};text-align:center;text-wrap:balance;max-width:90%;">${escapeHtml(name)}</div>` : ""}
        ${subtitle ? `<div style="font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${sm * 0.95}px;letter-spacing:4px;text-transform:uppercase;color:${accent};text-align:center;">${escapeHtml(subtitle)}</div>` : ""}
        <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.7}px;letter-spacing:8px;color:${withAlpha(themeColors.fg, 0.45)};">AUF ALLYVER</div>
      </div>`;
    }

    if (slot.mode === "image" && mediaSrc) {
      // Full-Bleed Cinematic: Bild deckt den ganzen Block, Text-Overlay unten
      return `<div style="position:absolute;inset:0;">
        <img src="${escapeHtml(mediaSrc)}" alt="${escapeHtml(name || brandName)}" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;" />
        <div style="position:absolute;inset:0;background:linear-gradient(180deg, rgba(0,0,0,0.0) 35%, rgba(0,0,0,0.45) 65%, rgba(0,0,0,0.85) 100%);"></div>
        <div style="position:absolute;left:0;right:0;bottom:120px;padding:0 80px;text-align:center;display:flex;flex-direction:column;gap:18px;align-items:center;">
          ${subtitle ? `<div style="font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${sm * 0.95}px;letter-spacing:5px;text-transform:uppercase;color:${accent};">${escapeHtml(subtitle)}</div>` : ""}
          ${name ? `<div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${lg * 0.95}px;letter-spacing:-2px;line-height:1.02;color:${themeColors.fg};text-shadow:0 4px 24px rgba(0,0,0,0.7);text-wrap:balance;max-width:90%;">${escapeHtml(name)}</div>` : ""}
          <div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.75}px;letter-spacing:10px;color:${accent};margin-top:6px;">ALLYVER</div>
        </div>
      </div>`;
    }

    if (slot.mode === "editorial" && mediaSrc) {
      // Magazine-Style: großes Bild oben (~62% Höhe) + Name unten + ALLYVER-Footer
      return `<div style="position:absolute;inset:0;display:flex;flex-direction:column;">
        <div style="position:relative;flex:0 0 62%;overflow:hidden;background:${withAlpha(themeColors.fg, 0.06)};">
          <img src="${escapeHtml(mediaSrc)}" alt="${escapeHtml(name || brandName)}" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;" />
          <div style="position:absolute;left:48px;top:48px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.7}px;letter-spacing:8px;color:${themeColors.fg};background:${withAlpha(themeColors.bg, 0.55)};backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);padding:8px 16px;border-radius:999px;">ALLYVER</div>
        </div>
        <div style="flex:1;display:flex;flex-direction:column;justify-content:center;align-items:center;gap:18px;padding:48px 64px;text-align:center;">
          ${subtitle ? `<div style="font-family:'Outfit',sans-serif;font-weight:${weights.semibold};font-size:${sm * 0.9}px;letter-spacing:5px;text-transform:uppercase;color:${accent};">${escapeHtml(subtitle)}</div>` : ""}
          ${name ? `<div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${md * 1.15}px;letter-spacing:-1.5px;line-height:1.02;color:${themeColors.fg};text-wrap:balance;max-width:96%;">${escapeHtml(name)}</div>` : ""}
        </div>
      </div>`;
    }

    // Default: text-Mode (bzw. Fallback wenn logo-Mode keinen logoSrc hatte)
    return `<div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${mega * allyverScale}px;letter-spacing:${allyverLetterSpacing}px;line-height:0.9;color:${accent};text-align:center;text-wrap:balance;width:100%;">${escapeHtml(brandName.toUpperCase())}</div>`;
  };

  const blocksHtml = blocks.map((b, i) => {
    const isAllyver = b.kind === "allyver";
    const slot = isAllyver ? b.slot : null;

    // Bei "image"-Mode wird der gesamte Block vom Bild abgedeckt → BG/Padding
    // werden im Render selbst gesetzt. Für die anderen Modi behalten wir den
    // klassischen Color-Block-Look.
    const isFullBleed = slot?.mode === "image" || slot?.mode === "editorial";
    const blockBg = isAllyver ? themeColors.bg : accent;
    const textColor = isAllyver ? accent : themeColors.bg;
    const cornerColor = isAllyver
      ? withAlpha(themeColors.fg, 0.55)
      : withAlpha(themeColors.bg, 0.6);
    const slashColor = isAllyver ? accent : themeColors.bg;

    let content: string;
    if (isAllyver && slot) {
      content = renderAllyverContent(slot);
    } else if (!isAllyver && b.kind === "question") {
      content = `<div style="font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${mega * questionScale}px;letter-spacing:${questionLetterSpacing}px;line-height:0.98;color:${textColor};text-align:center;text-wrap:balance;width:100%;hyphens:auto;-webkit-hyphens:auto;">${escapeHtml(b.text)}</div>`;
    } else {
      content = "";
    }

    const indexText = showIndex
      ? `${String(i + 1).padStart(2, "0")} / ${String(blocks.length).padStart(2, "0")}`
      : "";
    const isLast = i === blocks.length - 1;

    const paddingStyle = isFullBleed ? "padding:0;" : "padding:96px 80px;";

    return `<div id="${blockIds[i]}" style="position:absolute;inset:0;background:${blockBg};display:flex;align-items:center;justify-content:center;overflow:hidden;${paddingStyle}box-sizing:border-box;">
      <div id="${contentIds[i]}" style="position:relative;z-index:2;display:flex;align-items:center;justify-content:center;width:100%;${isFullBleed ? "height:100%;" : ""}">${content}</div>
      ${showIndex && !isFullBleed ? `<div style="position:absolute;top:48px;left:48px;font-family:'Outfit',sans-serif;font-weight:${weights.black};font-size:${sm * 0.7}px;letter-spacing:4px;color:${cornerColor};z-index:3;">${indexText}</div>` : ""}
      ${showSlash && !isFullBleed ? `<div style="position:absolute;bottom:48px;right:48px;width:60px;height:3px;background:${slashColor};transform:rotate(-45deg);opacity:0.7;z-index:3;"></div>` : ""}
      ${isLast && subline && !isFullBleed ? `<div style="position:absolute;bottom:80px;left:0;right:0;text-align:center;font-family:'Inter',sans-serif;font-weight:${weights.semibold};font-size:${sm * 0.85}px;color:${textColor};letter-spacing:0.4px;line-height:1.35;padding:0 64px;text-wrap:balance;z-index:3;">${escapeHtml(subline)}</div>` : ""}
    </div>`;
  }).join("");

  const html = `<div id="${wrapId}" class="clip" data-start="${clipStartSec.toFixed(4)}" data-duration="${clipDurationSec.toFixed(4)}" style="position:absolute;inset:0;overflow:hidden;">${blocksHtml}</div>`;

  // Each block starts fully wiped out (clipped). The visible inset target
  // is always `inset(0% 0% 0% 0%)`. Initial clip path determines the
  // direction the wipe APPEARS to come FROM.
  const clipFromLeft  = "inset(0% 100% 0% 0%)";
  const clipFromRight = "inset(0% 0% 0% 100%)";
  const clipFromTop   = "inset(100% 0% 0% 0%)";
  const clipFromBot   = "inset(0% 0% 100% 0%)";

  const hiddenClipFor = (i: number, kind: Block["kind"]): string => {
    if (i === 0) return "inset(0% 0% 0% 0%)";
    switch (wipeDirection) {
      case "left":  return clipFromLeft;
      case "right": return clipFromRight;
      case "up":    return clipFromBot;
      case "down":  return clipFromTop;
      case "alternate":
      default:
        return kind === "allyver" ? clipFromLeft : clipFromRight;
    }
  };

  const init = [
    ...blockIds.map((id, i) => gsapSetLine(`#${id}`, { clipPath: hiddenClipFor(i, blocks[i]!.kind) })),
    ...contentIds.map((id) => gsapSetLine(`#${id}`, { opacity: 0, y: 24, scale: 0.97 })),
  ].join("\n");

  const tweens: string[] = [];
  const slotSec = Math.max(0.85, clipDurationSec / Math.max(blocks.length, 1));
  blocks.forEach((_, i) => {
    const at = i * slotSec;
    if (i > 0) {
      tweens.push(t.to(`#${blockIds[i]}`, { clipPath: "inset(0% 0% 0% 0%)", duration: 0.55, ease: "raw:\"power3.inOut\"" }, at));
    }
    tweens.push(t.to(`#${contentIds[i]}`, { opacity: 1, y: 0, scale: 1, duration: 0.45, ease: "raw:\"power2.out\"" }, at + 0.22));
  });
  tweens.push(clipOutroFade(clipPrefix, clipDurationSec, clipStartSec, 14));

  return combineSceneFragments([backdrop, { html, init, tweens }]);
};
